import Transaction from '@/models/Transaction';

/** Movements in a currency that has no shekel figure, so they cannot be folded into the main total. */
export interface UnconvertedTotal {
  currency: string;
  amount: number;
  count: number;
}

export interface SelectionTotals {
  count: number;
  /** The currency the totals are expressed in: the fund's, or ILS when it has none. */
  currency: string;
  /** Income minus expenses, everything the board could express in `currency`. */
  net: number;
  /** Sum of the positive movements, in `currency`. */
  income: number;
  /** Sum of the negative movements, kept negative, in `currency`. */
  expenses: number;
  /** How many foreign movements went into the totals by their shekel amount. */
  convertedCount: number;
  unconverted: UnconvertedTotal[];
  /** HAT-1203: ticked rows that have not happened yet. They are inside `net`, and called out apart. */
  futureCount: number;
  /** What those rows contribute to `net`, in `currency`. A foreign future row the board cannot convert is not in it. */
  futureAmount: number;
}

const toCents = (value: number): number => Math.round(value * 100) / 100;

/**
 * Totals for the rows a coordinator ticked on the transactions board.
 *
 * A foreign-currency movement is counted by its shekel amount when the board is in
 * shekels - the same figure the export's "amount after conversion" column carries and
 * the amount cell shows in its tooltip. The sign follows the original amount, so a
 * dollar expense still pulls the total down. A movement in a currency the board
 * cannot convert is totalled on its own, apart from the main figure, rather than
 * being silently dropped or added as if it were shekels.
 */
export const summarizeSelectedTransactions = (transactions: Transaction[], fundCurrency?: string | null): SelectionTotals => {
  const currency = fundCurrency || 'ILS';
  let income = 0;
  let expenses = 0;
  let convertedCount = 0;
  let futureCount = 0;
  let futureAmount = 0;
  const unconverted = new Map<string, UnconvertedTotal>();

  for (const transaction of transactions) {
    const rowCurrency = transaction.currency || 'ILS';
    if (transaction.future) futureCount += 1;
    let amount: number | null = null;

    if (rowCurrency === currency) {
      amount = transaction.amount;
    } else if (currency === 'ILS' && transaction.ilsAmount != null) {
      // ilsAmount is not guaranteed to carry the sign; the original amount is.
      amount = transaction.amount < 0 ? -Math.abs(transaction.ilsAmount) : Math.abs(transaction.ilsAmount);
      convertedCount += 1;
    }

    if (amount == null) {
      const bucket = unconverted.get(rowCurrency) ?? { currency: rowCurrency, amount: 0, count: 0 };
      bucket.amount += transaction.amount;
      bucket.count += 1;
      unconverted.set(rowCurrency, bucket);
      continue;
    }

    if (amount < 0) expenses += amount;
    else income += amount;
    if (transaction.future) futureAmount += amount;
  }

  return {
    count: transactions.length,
    currency,
    net: toCents(income + expenses),
    income: toCents(income),
    expenses: toCents(expenses),
    convertedCount,
    unconverted: [...unconverted.values()].map((bucket) => ({ ...bucket, amount: toCents(bucket.amount) })),
    futureCount,
    futureAmount: toCents(futureAmount),
  };
};

/** The figure as the amount cell prints it: currency-formatted, two decimals, no stray bidi marks. */
export const formatSelectionAmount = (value: number, currency: string, locale: string): string =>
  value
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/[‎‏؜]/g, '');
